import { action, makeObservable, observable, runInAction } from "mobx";

import { useEffect } from "react";
import { useMatch } from "react-router";
import { isString } from "lodash-es";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { appRoutes } from "@/app/router";

export class AppStoreRoutingStore extends AppSubStore {
  noteIdParam: string | null;
  templateIdParam: string | null;
  collectionIdParam: string | null;
  sessionIdParam: string | null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.noteIdParam = null;
    this.templateIdParam = null;
    this.collectionIdParam = null;
    this.sessionIdParam = null;

    makeObservable(this, {
      noteIdParam: observable,
      templateIdParam: observable,
      collectionIdParam: observable,
      sessionIdParam: observable,
      setNoteIdParam: action,
      setTemplateIdParam: action,
      setCollectionIdParam: action,
      setSessionIdParam: action,
      useSyncRoutingParams: action,
    });
  }

  setNoteIdParam(maybeNoteIdParam: string | undefined) {
    if (!isString(maybeNoteIdParam)) {
      runInAction(() => {
        this.noteIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.noteIdParam = maybeNoteIdParam;
    });
  }

  setTemplateIdParam(maybeTemplateIdParam: string | undefined) {
    if (!isString(maybeTemplateIdParam)) {
      runInAction(() => {
        this.templateIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.templateIdParam = maybeTemplateIdParam;
    });
  }

  setSessionIdParam(maybeSessionIdParam: string | undefined) {
    if (!isString(maybeSessionIdParam)) {
      runInAction(() => {
        this.sessionIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.sessionIdParam = maybeSessionIdParam;
    });
  }

  setCollectionIdParam(maybeCollectionIdParam: string | undefined) {
    if (!isString(maybeCollectionIdParam)) {
      runInAction(() => {
        this.collectionIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.collectionIdParam = maybeCollectionIdParam;
    });
  }

  /**
   * We need to wire up each parameter independently, based on the target routes.
   */
  useSyncRoutingParams() {
    const noteIdMatch = useMatch(
      appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true })
    );

    const templateIdMatch = useMatch(
      appRoutes.templatesView({ params: { templateId: ":templateId" }, starPattern: true })
    );

    const collectionIdMatch = useMatch(
      appRoutes.collectionsView({ params: { collectionId: ":collectionId" }, starPattern: true })
    );

    const sessionIdMatch = useMatch(
      appRoutes.sessionsView({ params: { sessionId: ":sessionId" }, starPattern: true })
    );

    const maybeNoteIdParam = noteIdMatch?.params.noteId;
    const maybeCollectionIdParam = collectionIdMatch?.params.collectionId;
    const maybeTemplateIdParam = templateIdMatch?.params.templateId;
    const maybeSessionIdParam = sessionIdMatch?.params.sessionId;

    useEffect(() => {
      this.setNoteIdParam(maybeNoteIdParam);
    }, [maybeNoteIdParam]);

    useEffect(() => {
      this.setTemplateIdParam(maybeTemplateIdParam);
    }, [maybeTemplateIdParam]);

    useEffect(() => {
      this.setCollectionIdParam(maybeCollectionIdParam);
    }, [maybeCollectionIdParam]);

    useEffect(() => {
      this.setSessionIdParam(maybeSessionIdParam);
    }, [maybeSessionIdParam]);
  }
}
